import React, { useState } from 'react';
import './new.scss';
import Navbar from '../../components/navbar/Navbar';
import Sidebar from '../../components/sidebar/Sidebar';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { Upload } from "@aws-sdk/lib-storage";
import { S3Client } from "@aws-sdk/client-s3";
import { useNavigate } from 'react-router-dom';

const New = () => {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    desc: '',
    category: '',
    price: '',
    // Three separate request arrays
    addons: [{ id: 1, name: '', addOnPrice: '' }],
    specialRequest: [{ id: 1, name: '', addOnPrice: '' }],
    extraRequest: [{ id: 1, name: '', addOnPrice: '' }],
    schedulingEnabled: false,
    schedule: [
      { 
        timeOfDay: 'Morning Breakfast', 
        checked: false, 
        startTime: '', 
        endTime: '',
        startAP: 'AM',
        endAP: 'AM'
      },
      { 
        timeOfDay: 'Afternoon Lunch', 
        checked: false, 
        startTime: '', 
        endTime: '',
        startAP: 'AM',
        endAP: 'AM'
      },
      { 
        timeOfDay: 'Night Dinner', 
        checked: false, 
        startTime: '', 
        endTime: '',
        startAP: 'AM',
        endAP: 'AM'
      }
    ]
  });
  
  const categories = ['Main course', 'Side Dish', 'Snacks', 'Desserts', 'Appetizer', 'Alcohol', "Drinks"];
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Generic handlers for request arrays
  const addRequest = (type) => {
    const newItem = {
      id: formData[type].length + 1,
      name: '',
      addOnPrice: ''
    };
    setFormData({
      ...formData,
      [type]: [...formData[type], newItem]
    });
  };

  const deleteRequest = (type, id) => {
    const updated = formData[type].filter(item => item.id !== id);
    setFormData({
      ...formData,
      [type]: updated
    });
  };

  const handleRequestChange = (type, id, field, value) => {
    const updated = formData[type].map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setFormData({
      ...formData,
      [type]: updated
    });
  };

  // Schedule handlers
  const handleScheduleCheck = (index, checked) => {
    const updatedSchedule = formData.schedule.map((item, i) =>
      i === index ? { ...item, checked } : item
    );
    setFormData({
      ...formData,
      schedule: updatedSchedule
    });
  };

  const updateScheduleField = (index, field, value) => {
    const updatedSchedule = formData.schedule.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setFormData({
      ...formData,
      schedule: updatedSchedule
    });
  };

  // File handling
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (selectedFile) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(objectUrl);
    } else {
      setPreviewUrl('');
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.category || !formData.price) {
      alert('Please fill in required fields: Title, Category, and Price');
      return;
    }

    if (!file) {
      alert('Please select an image file');
      return;
    }

    try {
      setIsUploading(true);
      setUploadStatus('Uploading image to S3...');

      const s3FileUrl = await uploadToS3(file);
      
      // Prepare API data matching the backend schema
      const apiData = {
        title: formData.title,
        desc: formData.desc,
        categories: [formData.category],
        price: formData.price,
        // Send only non-empty items
        addons: formData.addons.filter(item => item.name.trim() !== ''),
        specialRequest: formData.specialRequest.filter(item => item.name.trim() !== ''),
        extraRequest: formData.extraRequest.filter(item => item.name.trim() !== ''),
        schedulingEnabled: formData.schedulingEnabled,
        schedule: formData.schedule.filter(item => item.checked).map(item => ({
          timeOfDay: item.timeOfDay,
          checked: item.checked,
          startTime: item.startTime,
          endTime: item.endTime,
          startAP: item.startAP,
          endAP: item.endAP
        })),
        img: s3FileUrl,
      };

      console.log('Submitting data:', apiData);

      const storedToken = localStorage.getItem('accessToken');
      if (!storedToken) {
        alert('You are not logged in. Please login first.');
        navigate('/login');
        return;
      }

      const response = await fetch(`${apiUrl}/foods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
        body: JSON.stringify(apiData),
      });

      const responseData = await response.json();

      if (response.ok) {
        alert('Food created successfully');
        navigate('/foods');
      } else {
        console.error('Error creating food:', responseData);
        alert(`Error: ${responseData.message || 'Failed to create food'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Upload to S3 (unchanged from original)
  const uploadToS3 = async (file) => {
    try {
      const fileName = `${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
      const key = `food/${fileName}`;
      
      console.log('Uploading to S3:', {
        bucket: process.env.REACT_APP_S3_BUCKET,
        region: process.env.REACT_APP_AWS_REGION,
        key: key,
        fileSize: file.size,
        fileType: file.type
      });

      if (!process.env.REACT_APP_AWS_ACCESS_KEY_ID || !process.env.REACT_APP_AWS_SECRET_ACCESS_KEY) {
        throw new Error('AWS credentials are missing. Check your .env file.');
      }

      const credentials = {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
      };

      const s3Client = new S3Client({
        region: process.env.REACT_APP_AWS_REGION,
        credentials: credentials
      });

      const parallelUploadS3 = new Upload({
        client: s3Client,
        params: {
          Bucket: process.env.REACT_APP_S3_BUCKET,
          Key: key,
          Body: file,
          ContentType: file.type,
        },
        tags: [],
        queueSize: 4,
        partSize: 5 * 1024 * 1024,
      });

      parallelUploadS3.on("httpUploadProgress", (progress) => {
        console.log('Upload progress:', progress);
        const percentage = Math.round((progress.loaded / progress.total) * 100);
        setUploadStatus(`Uploading... ${percentage}%`);
      });

      const result = await parallelUploadS3.done();
      console.log('Upload result:', result);

      const s3Url = `https://${process.env.REACT_APP_S3_BUCKET}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${key}`;
      
      setUploadStatus('Upload successful!');
      setPreviewUrl(s3Url);
      
      return s3Url;

    } catch (error) {
      console.error('S3 Upload Error:', error);
      setUploadStatus(`Upload failed: ${error.message}`);
      
      if (error.name === 'InvalidAccessKeyId') {
        alert('Invalid AWS Access Key. Check your .env file.');
      } else if (error.name === 'SignatureDoesNotMatch') {
        alert('AWS Secret Key is incorrect. Check your .env file.');
      } else if (error.name === 'NoSuchBucket') {
        alert('S3 bucket does not exist or you don\'t have access.');
      } else if (error.code === 'AccessDenied') {
        alert('Access denied. Check IAM permissions and CORS settings.');
      } else {
        alert(`Upload failed: ${error.message}`);
      }
      
      throw error;
    }
  };

  return (
    <div className="new">
      <Sidebar />
      <div className="newContainer">
        <Navbar />
        <div className="top">
          <h1>Add New Food</h1>
        </div>
        <div className="bottom">
          <div className="right">
            {/* Upload Status */}
            <div className="uploadStatus">
              {uploadStatus && (
                <div className={`status-message ${uploadStatus.includes('failed') ? 'error' : 'success'}`}>
                  {uploadStatus}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div className="formInput">
                <label>Title *</label>
                <input 
                  type="text" 
                  placeholder="Food title" 
                  value={formData.title} 
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              
              <div className="formInput">
                <label>Description</label>
                <input 
                  type="text" 
                  placeholder="Food Description" 
                  value={formData.desc} 
                  onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
                />
              </div>
              
              <div className="formInput">
                <label>Category *</label>
                <select 
                  value={formData.category} 
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="" disabled>Select a category</option>
                  {categories.map((category, index) => (
                    <option key={index} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Addons Section */}
              <div className="requestsSection">
                <h3>Addons</h3>
                {formData.addons.map((item, index) => (
                  <div key={item.id} className="requestItem">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => handleRequestChange('addons', item.id, 'name', e.target.value)}
                      placeholder={`Addon ${index + 1}`}
                      className="requestInput"
                    />
                    <input
                      type="number"
                      value={item.addOnPrice}
                      onChange={(e) => handleRequestChange('addons', item.id, 'addOnPrice', e.target.value)}
                      placeholder="Price"
                      className="priceInput"
                    />
                    {formData.addons.length > 1 && (
                      <button type="button" onClick={() => deleteRequest('addons', item.id)} className="deleteBtn">Delete</button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => addRequest('addons')} className="addRequestBtn">Add Addon</button>
              </div>

              {/* Special Requests Section */}
              <div className="requestsSection">
                <h3>Special Requests</h3>
                {formData.specialRequest.map((item, index) => (
                  <div key={item.id} className="requestItem">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => handleRequestChange('specialRequest', item.id, 'name', e.target.value)}
                      placeholder={`Special Request ${index + 1}`}
                      className="requestInput"
                    />
                    <input
                      type="number"
                      value={item.addOnPrice}
                      onChange={(e) => handleRequestChange('specialRequest', item.id, 'addOnPrice', e.target.value)}
                      placeholder="Price"
                      className="priceInput"
                    />
                    {formData.specialRequest.length > 1 && (
                      <button type="button" onClick={() => deleteRequest('specialRequest', item.id)} className="deleteBtn">Delete</button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => addRequest('specialRequest')} className="addRequestBtn">Add Special Request</button>
              </div>

              {/* Extra Requests Section */}
              <div className="requestsSection">
                <h3>Extra Requests</h3>
                {formData.extraRequest.map((item, index) => (
                  <div key={item.id} className="requestItem">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => handleRequestChange('extraRequest', item.id, 'name', e.target.value)}
                      placeholder={`Extra Request ${index + 1}`}
                      className="requestInput"
                    />
                    <input
                      type="number"
                      value={item.addOnPrice}
                      onChange={(e) => handleRequestChange('extraRequest', item.id, 'addOnPrice', e.target.value)}
                      placeholder="Price"
                      className="priceInput"
                    />
                    {formData.extraRequest.length > 1 && (
                      <button type="button" onClick={() => deleteRequest('extraRequest', item.id)} className="deleteBtn">Delete</button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => addRequest('extraRequest')} className="addRequestBtn">Add Extra Request</button>
              </div>

              <div className="formInput">
                <label>Price *</label>
                <input 
                  type="number" 
                  placeholder="Price (e.g., 12.50)" 
                  value={formData.price} 
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              {/* Image Upload */}
              <div className="formInput">
                <label htmlFor="file">
                  Food Image * <DriveFolderUploadIcon className="icon" />
                </label>
                <input 
                  type="file" 
                  id="file" 
                  style={{ display: 'none' }} 
                  onChange={handleFileChange}
                  accept="image/*"
                  required
                />
                {file && (
                  <div className="imagePreview">
                    <img src={previewUrl} alt="Preview" />
                    <p>{file.name} ({(file.size / 1024).toFixed(2)} KB)</p>
                  </div>
                )}
              </div>

              {/* Scheduling Section */}
              <div className="schedulingSection">
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    checked={formData.schedulingEnabled}
                    onChange={(e) => setFormData({ ...formData, schedulingEnabled: e.target.checked })}
                  />
                  Enable Scheduling
                </label>
                
                {formData.schedulingEnabled && (
                  <div className="scheduleOptions">
                    {formData.schedule.map((item, index) => (
                      <div key={index} className="scheduleItem">
                        <label className="scheduleLabel">
                          <input
                            type="checkbox"
                            checked={item.checked}
                            onChange={(e) => handleScheduleCheck(index, e.target.checked)}
                          />
                          {item.timeOfDay}
                        </label>
                        
                        {item.checked && (
                          <div className="timeInputs">
                            <div className="timeInputGroup">
                              <label>Start Time</label>
                              <input
                                type="time"
                                value={item.startTime}
                                onChange={(e) => updateScheduleField(index, 'startTime', e.target.value)}
                              />
                              <select
                                value={item.startAP}
                                onChange={(e) => updateScheduleField(index, 'startAP', e.target.value)}
                              >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                              </select>
                            </div>
                            
                            <div className="timeInputGroup">
                              <label>End Time</label>
                              <input
                                type="time"
                                value={item.endTime}
                                onChange={(e) => updateScheduleField(index, 'endTime', e.target.value)}
                              />
                              <select
                                value={item.endAP}
                                onChange={(e) => updateScheduleField(index, 'endAP', e.target.value)}
                              >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button 
                type="submit" 
                className="submitBtn"
                disabled={isUploading}
              >
                {isUploading ? 'Creating...' : 'Create Food Item'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default New;